import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import InvoiceGenerator from '../components/InvoiceGenerator';
import ReportGenerator from '../components/ReportGenerator';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const Reports = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventory, setInventory] = useState([]);
  const [sales, setSales] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [restocks, setRestocks] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [inventoryDateFilter, setInventoryDateFilter] = useState('');
  const [inventoryFilters, setInventoryFilters] = useState({
    articleNumber: '',
    colour: '',
    material: '',
    heel: ''
  });
  const [invoiceSearch, setInvoiceSearch] = useState({ number: '', date: '' });
  const [loading, setLoading] = useState(true);

  const filteredInventoryByDate = inventory.filter(item => {
    if (!inventoryDateFilter) return true;
    const itemDate = new Date(item.createdAt).toISOString().split('T')[0];
    return itemDate === inventoryDateFilter;
  }).filter(item => {
    return (
      (inventoryFilters.articleNumber === '' || item.articleNumber?.toLowerCase().includes(inventoryFilters.articleNumber.toLowerCase())) &&
      (inventoryFilters.colour === '' || item.colour?.toLowerCase().includes(inventoryFilters.colour.toLowerCase())) &&
      (inventoryFilters.material === '' || item.material?.toLowerCase().includes(inventoryFilters.material.toLowerCase())) &&
      (inventoryFilters.heel === '' || item.heel?.toLowerCase().includes(inventoryFilters.heel.toLowerCase()))
    );
  }).sort((a, b) => {
    // Sort by article number first, then by color
    const articleA = a.articleNumber || '';
    const articleB = b.articleNumber || '';
    if (articleA !== articleB) {
      return articleA.localeCompare(articleB, undefined, { numeric: true });
    }
    return (a.colour || '').localeCompare(b.colour || '');
  });

  const filteredInvoices = sales.filter(sale => {
    const matchesNumber = !invoiceSearch.number || sale.id.toString().includes(invoiceSearch.number);
    const matchesDate = !invoiceSearch.date || new Date(sale.createdAt).toISOString().split('T')[0] === invoiceSearch.date;
    return matchesNumber && matchesDate;
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [inventorySnapshot, salesSnapshot, ordersSnapshot, customersSnapshot, restocksSnapshot, companySnapshot] = await Promise.all([
        getDocs(collection(db, 'inventory')),
        getDocs(collection(db, 'sales')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'restocks')),
        getDocs(collection(db, 'company'))
      ]);
      
      const inventoryData = inventorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const salesData = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const customersData = customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const restocksData = restocksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const companyData = companySnapshot.docs.length > 0 ? companySnapshot.docs[0].data() : {};
      
      // Enhance customers data with order counts and spending
      const enhancedCustomers = customersData.map(customer => {
        const customerOrders = ordersData.filter(order => order.customerId === customer.id);
        const customerSales = salesData.filter(sale => sale.customerId === customer.id);
        const totalSpent = customerSales.reduce((sum, sale) => sum + (sale.total || 0), 0);
        
        return {
          ...customer,
          orderCount: customerOrders.length,
          totalSpent
        };
      });
      
      setInventory(inventoryData);
      setSales(salesData);
      setOrders(ordersData);
      setCustomers(enhancedCustomers);
      setRestocks(restocksData);
      setCompanyDetails(companyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="inventory-page">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  const getSalesReport = () => {
    const filteredSales = sales.filter(sale => {
      if (!dateRange.start && !dateRange.end) return true;
      const saleDate = new Date(sale.createdAt).toISOString().split('T')[0];
      if (dateRange.start && saleDate < dateRange.start) return false;
      if (dateRange.end && saleDate > dateRange.end) return false;
      return true;
    });
    
    return {
      sales: filteredSales,
      totalSales: filteredSales.length,
      totalRevenue: filteredSales.reduce((sum, sale) => sum + (sale.total || sale.totalAmount || 0), 0),
      totalDiscount: filteredSales.reduce((sum, sale) => sum + ((sale.subtotal || 0) * (sale.discount || 0) / 100), 0)
    };
  };

  const getOrdersReport = () => {
    const filteredOrders = orders.filter(order => {
      if (!dateRange.start && !dateRange.end) return true;
      const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
      if (dateRange.start && orderDate < dateRange.start) return false;
      if (dateRange.end && orderDate > dateRange.end) return false;
      return true;
    });
    
    return filteredOrders;
  };
  
  const getCustomersReport = () => {
    return customers.filter(customer => {
      if (!dateRange.start && !dateRange.end) return true;
      const customerDate = new Date(customer.createdAt).toISOString().split('T')[0];
      if (dateRange.start && customerDate < dateRange.start) return false;
      if (dateRange.end && customerDate > dateRange.end) return false;
      return true;
    });
  };

  const salesReport = getSalesReport();
  const ordersReport = getOrdersReport();
  const customersReport = getCustomersReport();

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />Back
          </button>
          <h1 className="inventory-title">Reports & Analytics</h1>
        </div>
      </div>

      <div style={{ padding: '20px 30px', background: '#f8f9fa' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            onClick={() => setActiveTab('inventory')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'inventory' ? '#667eea' : '#e9ecef',
              color: activeTab === 'inventory' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Inventory Report
          </button>
          <button 
            onClick={() => setActiveTab('sales')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'sales' ? '#667eea' : '#e9ecef',
              color: activeTab === 'sales' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Sales Report
          </button>
          <button 
            onClick={() => setActiveTab('orders')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'orders' ? '#667eea' : '#e9ecef',
              color: activeTab === 'orders' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Orders Report
          </button>
          <button 
            onClick={() => setActiveTab('restocks')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'restocks' ? '#667eea' : '#e9ecef',
              color: activeTab === 'restocks' ? 'white' : '#666',
              cursor: 'pointer'
            }}
          >
            Restocks Report
          </button>
        </div>

        {activeTab === 'inventory' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Inventory Filters</h3>
                <ReportGenerator 
                  reportType="inventory" 
                  data={filteredInventoryByDate} 
                  dateRange={{ start: inventoryDateFilter, end: inventoryDateFilter }} 
                  companyDetails={companyDetails} 
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <input 
                  type="text" 
                  placeholder="Article Number"
                  value={inventoryFilters.articleNumber}
                  onChange={(e) => setInventoryFilters({...inventoryFilters, articleNumber: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <input 
                  type="text" 
                  placeholder="Colour"
                  value={inventoryFilters.colour}
                  onChange={(e) => setInventoryFilters({...inventoryFilters, colour: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <input 
                  type="text" 
                  placeholder="Material"
                  value={inventoryFilters.material}
                  onChange={(e) => setInventoryFilters({...inventoryFilters, material: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <input 
                  type="text" 
                  placeholder="Heel Type"
                  value={inventoryFilters.heel}
                  onChange={(e) => setInventoryFilters({...inventoryFilters, heel: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <input 
                  type="date" 
                  value={inventoryDateFilter} 
                  onChange={(e) => setInventoryDateFilter(e.target.value)}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={() => {
                    setInventoryFilters({ articleNumber: '', colour: '', material: '', heel: '' });
                    setInventoryDateFilter('');
                  }}
                  className="btn btn-secondary"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Total Items</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{filteredInventoryByDate.length}</div>
                <small style={{ color: '#666' }}>{inventoryDateFilter ? `Added on ${new Date(inventoryDateFilter).toLocaleDateString()}` : 'All time'}</small>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#43e97b', margin: '0 0 10px 0' }}>Total Packs</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{filteredInventoryByDate.reduce((sum, item) => sum + (item.packsInStock || 0), 0)}</div>
                <small style={{ color: '#666' }}>Available in stock</small>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#fa709a', margin: '0 0 10px 0' }}>Total Pairs</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{filteredInventoryByDate.reduce((sum, item) => sum + ((item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0)), 0)}</div>
                <small style={{ color: '#666' }}>Individual pairs</small>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Stock Value</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>LKR {filteredInventoryByDate.reduce((sum, item) => sum + (((item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0)) * item.pricePerPair), 0).toLocaleString()}</div>
                <small style={{ color: '#666' }}>Total inventory value</small>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>Inventory Details ({filteredInventoryByDate.length} items)</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Article</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Color</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Full Packs</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Loose Pairs</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Pairs Needed</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Added Date</th>
                      <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>Stock Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInventoryByDate.map(item => {
                      const totalPairs = (item.packsInStock * item.pairsPerPack) + (item.loosePairs || 0);
                      const fullPacks = Math.floor(totalPairs / item.pairsPerPack);
                      const loosePairs = totalPairs % item.pairsPerPack;
                      const pairsNeeded = loosePairs > 0 ? (item.pairsPerPack - loosePairs) : 0;
                      const stockValue = totalPairs * item.pricePerPair;
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>({item.articleNumber})</td>
                          <td style={{ padding: '12px', border: '1px solid #ddd' }}>{item.colour}</td>
                          <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{fullPacks}</td>
                          <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{loosePairs}</td>
                          <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', color: pairsNeeded > 0 ? '#e74c3c' : '#4caf50' }}>
                            {pairsNeeded > 0 ? pairsNeeded : 'Complete'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{new Date(item.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>LKR {stockValue.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sales' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Date Range Filter</h3>
                <ReportGenerator 
                  reportType="sales" 
                  data={salesReport} 
                  dateRange={dateRange} 
                  companyDetails={companyDetails} 
                />
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <input 
                  type="date" 
                  value={dateRange.start} 
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <span>to</span>
                <input 
                  type="date" 
                  value={dateRange.end} 
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <button 
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Total Sales</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{salesReport.totalSales}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#43e97b', margin: '0 0 10px 0' }}>Total Revenue</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>LKR {salesReport.totalRevenue.toLocaleString()}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#fa709a', margin: '0 0 10px 0' }}>Total Discounts</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>LKR {salesReport.totalDiscount.toLocaleString()}</div>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>Sales Details</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Sale ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Customer</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Items</th>
                      <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>Total</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesReport.sales.map(sale => (
                      <tr key={sale.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>#{sale.id}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{sale.customerName}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{new Date(sale.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{sale.items?.length || 0}</td>
                        <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>LKR {(sale.total || 0).toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{sale.paymentMethod} ({sale.paymentStatus})</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Date Range Filter</h3>
                <ReportGenerator 
                  reportType="orders" 
                  data={ordersReport} 
                  dateRange={dateRange} 
                  companyDetails={companyDetails} 
                />
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <input 
                  type="date" 
                  value={dateRange.start} 
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <span>to</span>
                <input 
                  type="date" 
                  value={dateRange.end} 
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <button 
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Total Orders</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{ordersReport.length}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#43e97b', margin: '0 0 10px 0' }}>Total Value</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>LKR {ordersReport.reduce((sum, order) => sum + (order.total || 0), 0).toLocaleString()}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#fa709a', margin: '0 0 10px 0' }}>Pending Orders</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{ordersReport.filter(o => o.status === 'pending').length}</div>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>Orders Details</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Order ID</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Customer</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Items</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersReport.map(order => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>#{String(order.id).slice(-6)}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{order.customerName}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{order.items?.length || 0}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{order.status.toUpperCase()}</td>
                        <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>LKR {(order.total || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'customers' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0 }}>Date Range Filter</h3>
                <ReportGenerator 
                  reportType="customers" 
                  data={customersReport} 
                  dateRange={dateRange} 
                  companyDetails={companyDetails} 
                />
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <input 
                  type="date" 
                  value={dateRange.start} 
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <span>to</span>
                <input 
                  type="date" 
                  value={dateRange.end} 
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <button 
                  onClick={() => setDateRange({ start: '', end: '' })}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Total Customers</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{customersReport.length}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#43e97b', margin: '0 0 10px 0' }}>Total Revenue</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>LKR {customersReport.reduce((sum, c) => sum + (c.totalSpent || 0), 0).toLocaleString()}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#fa709a', margin: '0 0 10px 0' }}>Avg Orders/Customer</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{(customersReport.reduce((sum, c) => sum + (c.orderCount || 0), 0) / customersReport.length || 0).toFixed(1)}</div>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>Customers Details</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Shop Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>City</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Phone</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Orders</th>
                      <th style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>Total Spent</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customersReport.map(customer => (
                      <tr key={customer.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{customer.shopName}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{customer.city}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{customer.phoneNumber || 'N/A'}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{customer.orderCount || 0}</td>
                        <td style={{ padding: '12px', textAlign: 'right', border: '1px solid #ddd' }}>LKR {(customer.totalSpent || 0).toLocaleString()}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{new Date(customer.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'restocks' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '15px' }}>Restock Records</h3>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#667eea', margin: '0 0 10px 0' }}>Total Restocks</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{restocks.length}</div>
              </div>
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h3 style={{ color: '#43e97b', margin: '0 0 10px 0' }}>Total Pairs Added</h3>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#333' }}>{restocks.reduce((sum, r) => sum + (r.totalPairsAdded || 0), 0)}</div>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>Restock History</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Date</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Article</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Colour</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Packs Added</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Pairs Added</th>
                      <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Total Pairs</th>
                      <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restocks.map(restock => (
                      <tr key={restock.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{new Date(restock.restockedAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{restock.articleNumber}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{restock.colour}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{restock.packsAdded || 0}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{restock.loosePairsAdded || 0}</td>
                        <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>{restock.totalPairsAdded || 0}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{restock.restockedBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div>
            <div style={{ background: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '15px' }}>Search Invoices</h3>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input 
                  type="text" 
                  placeholder="Search by Invoice #"
                  value={invoiceSearch.number}
                  onChange={(e) => setInvoiceSearch({...invoiceSearch, number: e.target.value})}
                  style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', minWidth: '150px' }}
                />
                <input 
                  type="date" 
                  value={invoiceSearch.date} 
                  onChange={(e) => setInvoiceSearch({...invoiceSearch, date: e.target.value})}
                  style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                />
                <button 
                  onClick={() => setInvoiceSearch({ number: '', date: '' })}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginBottom: '20px' }}>All Invoices ({filteredInvoices.length})</h3>
              <div className="inventory-grid">
                {filteredInvoices.map(sale => (
                  <div key={sale.id} className="inventory-item">
                    <div className="item-header">
                      <div className="item-article">Invoice #{sale.id}</div>
                      <div className="item-stock">LKR {(sale.total || 0).toLocaleString()}</div>
                    </div>
                    <div className="item-details">
                      <div className="item-detail">
                        <span className="item-detail-label">Customer:</span>
                        <span className="item-detail-value">{sale.customerName}</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Date:</span>
                        <span className="item-detail-value">{new Date(sale.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Items:</span>
                        <span className="item-detail-value">{sale.items?.length} products</span>
                      </div>
                      <div className="item-detail">
                        <span className="item-detail-label">Payment:</span>
                        <span className="item-detail-value">{sale.paymentMethod} ({sale.paymentStatus})</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                      <button onClick={() => setSelectedSale(sale)} style={{ background: '#667eea', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Eye size={14} /> View Invoice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {filteredInvoices.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No invoices found matching your search criteria.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {selectedSale && (
        <InvoiceGenerator 
          sale={selectedSale} 
          companyDetails={companyDetails} 
          onClose={() => setSelectedSale(null)} 
        />
      )}
    </div>
  );
};

export default Reports;